import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import SelectSearch from 'react-select-search';
import Fuse from 'fuse.js';
import { FiPlus, FiEdit2, FiTrash2, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import axios from 'axios';
import { BASE_URL } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import { fetchAdminLeagueStructure } from '../actions/leaguesActions';
import { buildLeagueStructure } from '../utilities/leagueStructure';
import {
  isGroupLeague, isGroupStage, canHaveGroupStages, priorityForPhaseChange, NO_GROUP_STAGES_TEXT,
} from '../utilities/stagePriority';
import CreateStageModal from '../components/CreateStageModal';
import CreateMultipleStagesModal from '../components/CreateMultipleStagesModal';
import EditStageModal from '../components/EditStageModal';
import GenerateTourModal from '../components/GenerateTourModal';
import CreateMatchModal from '../components/CreateMatchModal';
import '../styles/stagesManagement.css';
import Loader from "react-loader-spinner";
import moment from 'moment';

function StagesManagement() {
  const history = useHistory();
  const dispatch = useDispatch();

  // Восстанавливаем состояние из localStorage
  const savedState = JSON.parse(localStorage.getItem('stagesManagementState') || '{}');

  const [championships, setChampionships] = useState([]);
  const [selectedChampionship, setSelectedChampionship] = useState(savedState.championship || null);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(savedState.league || null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(savedState.season || null);
  const [stages, setStages] = useState([]);
  const [movingStageId, setMovingStageId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedStageId, setExpandedStageId] = useState(savedState.expandedStageId || null);
  const [stageMatches, setStageMatches] = useState({});
  // Формат сезона из league_settings: тип лиги и сколько туров идёт в группу
  const [stageFormat, setStageFormat] = useState(null);
  
  // Модальные окна
  const [isCreateStageModalOpen, setIsCreateStageModalOpen] = useState(false);
  const [isCreateMultipleModalOpen, setIsCreateMultipleModalOpen] = useState(false);
  const [isEditStageModalOpen, setIsEditStageModalOpen] = useState(false);
  const [isGenerateTourModalOpen, setIsGenerateTourModalOpen] = useState(false);
  const [isCreateMatchModalOpen, setIsCreateMatchModalOpen] = useState(false);
  const [selectedStageForEdit, setSelectedStageForEdit] = useState(null);
  const [selectedStageForGenerate, setSelectedStageForGenerate] = useState(null);
  const [selectedStageForMatch, setSelectedStageForMatch] = useState(null);

  // Сохраняем состояние в localStorage при изменении
  useEffect(() => {
    if (selectedChampionship || selectedLeague || selectedSeason || expandedStageId) {
      localStorage.setItem('stagesManagementState', JSON.stringify({
        championship: selectedChampionship,
        league: selectedLeague,
        season: selectedSeason,
        expandedStageId: expandedStageId
      }));
    }
  }, [selectedChampionship, selectedLeague, selectedSeason, expandedStageId]);

  useEffect(() => {
    loadChampionships();
  }, []);

  useEffect(() => {
    if (selectedLeague && selectedSeason) {
      loadStages();
    }
  }, [selectedLeague, selectedSeason]);

  useEffect(() => {
    setStageFormat(null);
    if (selectedLeague && selectedSeason) {
      loadStageFormat(selectedLeague.value, selectedSeason.value);
    }
  }, [selectedLeague, selectedSeason]);

  // Загружаем матчи для раскрытой стадии после загрузки стадий
  useEffect(() => {
    if (expandedStageId && stages.length > 0 && !stageMatches[expandedStageId]) {
      loadStageMatches(expandedStageId);
    }
  }, [expandedStageId, stages]);

  const loadChampionships = async () => {
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();

      const response = await axios.post(
        BASE_URL.url + '/leagues/1/adminLeagues',
        {},
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );

      const leaguesData = Array.isArray(response.data) ? response.data : response.data.data;

      if (leaguesData && leaguesData.length > 0) {
        // Группируем по championat_name (у нас нет championat_id)
        const championsMap = {};
        
        leaguesData.forEach(item => {
          const championatName = item.championat_name || 'Без чемпионата';
          
          if (!championsMap[championatName]) {
            championsMap[championatName] = {
              value: championatName, // Используем название как value
              name: championatName,
              leagues: []
            };
          }
          
          if (item.league_id > 0) {
            championsMap[championatName].leagues.push({
              value: item.league_id,
              name: item.league_name,
              seasons: item.seasons || [],
              ...item
            });
          }
        });

        const formattedChampionships = Object.values(championsMap)
          .filter(c => c.leagues.length > 0)
          .map((item, index) => ({
            ...item,
            key: index
          }));

        setChampionships(formattedChampionships);
        
        // Восстанавливаем выбранный чемпионат если он был сохранен
        if (savedState.championship) {
          const restoredChampionship = formattedChampionships.find(
            c => c.value === savedState.championship.value
          );
          if (restoredChampionship) {
            setSelectedChampionship(restoredChampionship);
            setLeagues(restoredChampionship.leagues);
            
            // Восстанавливаем лигу
            if (savedState.league) {
              const restoredLeague = restoredChampionship.leagues.find(
                l => l.value === savedState.league.value
              );
              if (restoredLeague) {
                setSelectedLeague(restoredLeague);
                const formattedSeasons = restoredLeague.seasons.map((item, index) => ({
                  value: item.season_id,
                  name: item.season_name,
                  key: index,
                  ...item
                }));
                setSeasons(formattedSeasons);
                
                // Восстанавливаем сезон
                if (savedState.season) {
                  const restoredSeason = formattedSeasons.find(
                    s => s.value === savedState.season.value
                  );
                  if (restoredSeason) {
                    setSelectedSeason(restoredSeason);
                  }
                }
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading championships:', error);
    }
  };

  const handleChampionshipChange = (championship_id) => {
    const championship = championships.find(c => c.value === championship_id);
    setSelectedChampionship(championship);
    setSelectedLeague(null);
    setSelectedSeason(null);
    setStages([]);
    setExpandedStageId(null);

    if (championship && championship.leagues && championship.leagues.length > 0) {
      setLeagues(championship.leagues);
    } else {
      setLeagues([]);
    }
  };

  const handleLeagueChange = (league_id) => {
    const league = leagues.find(l => l.value === league_id);
    setSelectedLeague(league);
    setSelectedSeason(null);
    setStages([]);
    setExpandedStageId(null);

    if (league && league.seasons && league.seasons.length > 0) {
      const formattedSeasons = league.seasons.map((item, index) => ({
        value: item.season_id,
        name: item.season_name,
        key: index,
        ...item
      }));
      setSeasons(formattedSeasons);
    } else {
      setSeasons([]);
    }
  };

  const handleSeasonChange = (season_id) => {
    const season = seasons.find(s => s.value === season_id);
    setSelectedSeason(season);
    setExpandedStageId(null);
  };

  const loadStages = async () => {
    if (!selectedLeague || !selectedSeason) return;

    setLoading(true);
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();

      const response = await axios.post(
        BASE_URL.url + `/leagues/${selectedLeague.value}/getLeagueStages`,
        {
          league_id: selectedLeague.value,
          season_id: selectedSeason.value
        },
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );

      const stagesData = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setStages(stagesData);
    } catch (error) {
      console.error('Error loading stages:', error);
      setStages([]);
    } finally {
      setLoading(false);
    }
  };

  // Настройки сезона берём из adminLeagueStructure: там они по каждому сезону,
  // а в списке adminLeagues — только на уровне лиги
  const loadStageFormat = async (leagueId, seasonId) => {
    try {
      const data = await dispatch(fetchAdminLeagueStructure());
      const league = buildLeagueStructure(data).leaguesById[Number(leagueId)];
      const settings = league && league.settings[Number(seasonId)];
      if (!settings) return;
      setStageFormat({
        leagueId: Number(leagueId),
        seasonId: Number(seasonId),
        leagueType: settings.league_type,
        groupStagesAmount: Number(settings.group_stages_amount) || 0,
      });
    } catch (error) {
      console.error('Error loading season settings:', error);
    }
  };

  // Формат только для выбранной пары лига+сезон: ответ от прошлого выбора не подставляем
  const currentFormat = stageFormat
    && selectedLeague && selectedSeason
    && stageFormat.leagueId === Number(selectedLeague.value)
    && stageFormat.seasonId === Number(selectedSeason.value)
    ? stageFormat : null;

  const loadStageMatches = async (stageId) => {
    if (stageMatches[stageId]) return; // Уже загружено

    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();

      // Используем тот же метод, что и в мобильном приложении
      const response = await axios.post(
        BASE_URL.url + `/leagues/${selectedLeague.value}/matches`,
        {
          season_id: selectedSeason.value,
          stage_id: stageId
        },
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );

      const matchesData = Array.isArray(response.data) ? response.data : (response.data.data || []);
      
      // Фильтруем матчи по stage_id
      const filteredMatches = matchesData.filter(match => 
        match.stage_id === stageId || match.tour_id === stageId
      );
      
      setStageMatches(prev => ({
        ...prev,
        [stageId]: filteredMatches
      }));
    } catch (error) {
      console.error('Error loading stage matches:', error);
      setStageMatches(prev => ({
        ...prev,
        [stageId]: []
      }));
    }
  };

  const toggleStage = async (stageId) => {
    if (expandedStageId === stageId) {
      setExpandedStageId(null);
    } else {
      setExpandedStageId(stageId);
      
      // Проверяем, есть ли матчи в самой стадии
      const stage = stages.find(s => s.id === stageId);
      
      if (stage && stage.matches && stage.matches.length > 0) {
        // Матчи уже есть в объекте стадии
        setStageMatches(prev => ({
          ...prev,
          [stageId]: stage.matches
        }));
      } else if (!stageMatches[stageId]) {
        // Загружаем матчи через API
        await loadStageMatches(stageId);
      }
    }
  };

  const handleEditStage = (stage) => {
    setSelectedStageForEdit(stage);
    setIsEditStageModalOpen(true);
  };

  const handleGenerateTour = (stage) => {
    setSelectedStageForGenerate(stage);
    setIsGenerateTourModalOpen(true);
  };

  const handleCreateMatch = (stage) => {
    setSelectedStageForMatch(stage);
    setIsCreateMatchModalOpen(true);
  };

  const handleMatchCreated = async () => {
    setIsCreateMatchModalOpen(false);
    
    // Перезагружаем матчи для текущей стадии
    if (selectedStageForMatch && selectedLeague && selectedSeason) {
      const stageId = selectedStageForMatch.id;
      
      try {
        const isTokenExpired = checkIfTokenExpired();
        if (isTokenExpired) {
          await dispatch(refreshTokens());
        }
        const tokens = getTokensFromStore();

        const response = await axios.post(
          BASE_URL.url + `/leagues/${selectedLeague.value}/matches`,
          {
            season_id: selectedSeason.value,
            stage_id: stageId
          },
          {
            headers: {
              Authorization: "Bearer " + tokens.access_token,
              Accept: 'application/json',
              'Content-Type': 'application/json',
            }
          }
        );

        const matchesData = Array.isArray(response.data) ? response.data : (response.data.data || []);
        const filteredMatches = matchesData.filter(match => 
          match.stage_id === stageId || match.tour_id === stageId
        );
        
        setStageMatches(prev => ({
          ...prev,
          [stageId]: filteredMatches
        }));
        
        // Раскрываем стадию если она была закрыта
        setExpandedStageId(stageId);
      } catch (error) {
        console.error('Ошибка при загрузке матчей:', error);
      }
    }
  };

  const handleTourGenerated = async () => {
    setIsGenerateTourModalOpen(false);
    
    // Принудительно перезагружаем матчи для раскрытой стадии
    if (selectedStageForGenerate && selectedLeague && selectedSeason) {
      const stageId = selectedStageForGenerate.id;
      
      try {
        const isTokenExpired = checkIfTokenExpired();
        if (isTokenExpired) {
          await dispatch(refreshTokens());
        }
        const tokens = getTokensFromStore();

        const response = await axios.post(
          BASE_URL.url + `/leagues/${selectedLeague.value}/matches`,
          {
            season_id: selectedSeason.value,
            stage_id: stageId
          },
          {
            headers: {
              Authorization: "Bearer " + tokens.access_token,
              Accept: 'application/json',
              'Content-Type': 'application/json',
            }
          }
        );

        const matchesData = Array.isArray(response.data) ? response.data : (response.data.data || []);
        
        // Фильтруем матчи по stage_id
        const filteredMatches = matchesData.filter(match => 
          match.stage_id === stageId || match.tour_id === stageId
        );
        
        setStageMatches(prev => ({
          ...prev,
          [stageId]: filteredMatches
        }));
        
        // Раскрываем стадию если она была закрыта
        setExpandedStageId(stageId);
      } catch (error) {
        console.error('Ошибка при загрузке матчей:', error);
        setStageMatches(prev => ({
          ...prev,
          [stageId]: []
        }));
      }
    }
  };

  // Перенос стадии между групповым этапом и плей-офф: меняется только priority
  // (changeStage с пустым matches матчи не трогает — FRONTEND_API_CHANGES §3.3)
  const moveStagePhase = async (stage, phase) => {
    if (!currentFormat) return;
    if (phase === 'group' && !canHaveGroupStages(currentFormat)) {
      alert(NO_GROUP_STAGES_TEXT);
      return;
    }
    const question = phase === 'group'
      ? `Перенести «${stage.name}» в групповой этап? Её матчи пойдут в таблицу групп.`
      : `Перенести «${stage.name}» в плей-офф? Её матчи перестанут учитываться в таблице групп.`;
    if (!window.confirm(question)) return;
    setMovingStageId(stage.id);
    try {
      if (checkIfTokenExpired()) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();
      await axios.post(
        BASE_URL.url + `/leagues/changeStage/${stage.id}`,
        {
          name: stage.name,
          short_name: stage.short_name || null,
          priority: priorityForPhaseChange(stages, currentFormat, stage, phase),
          group_id: stage.group_id || null,
          league_id: parseInt(selectedLeague.value, 10),
          season_id: parseInt(selectedSeason.value, 10),
          stages_amount: 1,
          matches: [],
        },
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );
      await loadStages();
    } catch (error) {
      console.error('Error moving stage:', error);
      const data = error.response?.data;
      alert('Не удалось перенести стадию: ' + ((typeof data === 'string' && data) || data?.message || data?.error || error.message));
    } finally {
      setMovingStageId(null);
    }
  };

  const handleDeleteStage = async (stageId) => {
    try {
      // Сначала проверяем, есть ли матчи в стадии
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();

      // Загружаем матчи стадии
      const matchesResponse = await axios.post(
        BASE_URL.url + `/leagues/${selectedLeague.value}/matches`,
        {
          season_id: selectedSeason.value,
          stage_id: stageId
        },
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );

      const matchesData = Array.isArray(matchesResponse.data) ? matchesResponse.data : (matchesResponse.data.data || []);
      // id могут прийти строкой: строгое сравнение пропускало матчи,
      // и стадия удалялась вместе с висящими матчами
      const stageMatchesCount = matchesData.filter(match =>
        Number(match.stage_id) === Number(stageId) || Number(match.tour_id) === Number(stageId)
      ).length;

      if (stageMatchesCount > 0) {
        alert(`Невозможно удалить стадию. В ней содержится ${stageMatchesCount} матчей. Сначала удалите все матчи.`);
        return;
      }

      if (!window.confirm('Вы уверены, что хотите удалить эту стадию?')) {
        return;
      }

      await axios.post(
        BASE_URL.url + `/leagues/deleteStage/${stageId}`,
        {},
        {
          headers: {
            Authorization: "Bearer " + tokens.access_token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      );

      loadStages(); // Перезагружаем список
    } catch (error) {
      console.error('Error deleting stage:', error);
      alert('Ошибка при удалении стадии');
    }
  };

  const handleStageCreated = () => {
    // Перезагружаем список стадий
    loadStages();
    // Сбрасываем expandedStageId и stageMatches, чтобы пользователь мог заново раскрыть стадию
    setExpandedStageId(null);
    setStageMatches({});
  };

  const goToMatch = (matchId) => {
    history.push(`/manage_match/${matchId}`);
  };

  const fuzzySearch = (options) => {
    const fuse = new Fuse(options, {
      keys: ['name'],
      threshold: 0.3,
    });

    return (value) => {
      if (!value.length) {
        return options;
      }
      return fuse.search(value).map(item => item.item);
    };
  };

  const renderMatchState = (match) => {
    if (match.status === 2) return 'Завершен';
    if (match.status === 1) return 'Идет';
    if (match.status === 0) return 'Не начат';
    return 'Не определен';
  };

  return (
    <div className="layoutWrapper">
      <div className="stages-management-container">
        <div className="stages-header">
          <h1>Управление стадиями</h1>
        </div>

        <div className="stages-filters">
          <div className="filter-group">
            <label>Чемпионат</label>
            <SelectSearch
              options={championships}
              search
              value={selectedChampionship?.value}
              onChange={handleChampionshipChange}
              filterOptions={fuzzySearch}
              emptyMessage="Чемпионат не найден"
              placeholder="Выберите чемпионат"
            />
          </div>

          {selectedChampionship && leagues.length > 0 && (
            <div className="filter-group">
              <label>Лига</label>
              <SelectSearch
                options={leagues}
                search
                value={selectedLeague?.value}
                onChange={handleLeagueChange}
                filterOptions={fuzzySearch}
                emptyMessage="Лига не найдена"
                placeholder="Выберите лигу"
              />
            </div>
          )}

          {selectedLeague && seasons.length > 0 && (
            <div className="filter-group">
              <label>Сезон</label>
              <SelectSearch
                options={seasons}
                search
                value={selectedSeason?.value}
                onChange={handleSeasonChange}
                filterOptions={fuzzySearch}
                emptyMessage="Сезон не найден"
                placeholder="Выберите сезон"
              />
            </div>
          )}

          {selectedLeague && selectedSeason && (
            <div className="stages-actions">
              <button
                className="btn-create-stage"
                onClick={() => setIsCreateStageModalOpen(true)}
              >
                <FiPlus /> Создать стадию
              </button>
              <button
                className="btn-create-multiple"
                onClick={() => setIsCreateMultipleModalOpen(true)}
              >
                <FiPlus /> Создать несколько
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="stages-loading">
            <Loader type="Rings" color="#3182ce" height={50} width={50} />
            <span>Загрузка стадий...</span>
          </div>
        ) : selectedLeague && selectedSeason ? (
          stages.length > 0 ? (
            <div className="stages-list">
              {stages.map((stage) => (
                <div key={stage.id} className="stage-card">
                  <div className="stage-card-header" onClick={() => toggleStage(stage.id)}>
                    <div className="stage-info">
                      <h3>{stage.name}</h3>
                      {stage.short_name && (
                        <span className="stage-short-name">({stage.short_name})</span>
                      )}
                      <span className="stage-priority">№ {stage.priority || '—'}</span>
                      {isGroupLeague(currentFormat) && (
                        isGroupStage(stage.priority, currentFormat)
                          ? <span className="stage-phase stage-phase-group">группа</span>
                          : <span className="stage-phase stage-phase-playoff">плей-офф</span>
                      )}
                      {isGroupLeague(currentFormat) && (
                        <button
                          type="button"
                          className="stage-phase-move"
                          disabled={movingStageId === stage.id}
                          title={isGroupStage(stage.priority, currentFormat)
                            ? 'Перенести стадию в плей-офф'
                            : 'Перенести стадию в групповой этап: её матчи пойдут в таблицу групп'}
                          onClick={(e) => {
                            e.stopPropagation();
                            moveStagePhase(stage, isGroupStage(stage.priority, currentFormat) ? 'playoff' : 'group');
                          }}
                        >
                          {movingStageId === stage.id
                            ? 'Переносим…'
                            : isGroupStage(stage.priority, currentFormat) ? '→ в плей-офф' : '→ в группу'}
                        </button>
                      )}
                    </div>
                    <div className="stage-actions">
                      <button
                        className="btn-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditStage(stage);
                        }}
                      >
                        <FiEdit2 />
                      </button>
                      {(!stageMatches[stage.id] || stageMatches[stage.id].length === 0) && (
                        <button
                          className="btn-icon btn-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteStage(stage.id);
                          }}
                        >
                          <FiTrash2 />
                        </button>
                      )}
                      <button className="btn-icon btn-expand">
                        {expandedStageId === stage.id ? <FiChevronUp /> : <FiChevronDown />}
                      </button>
                    </div>
                  </div>

                  {expandedStageId === stage.id && (
                    <div className="stage-card-body">
                      {/* Кнопки действий */}
                      <div className="generate-tour-section">
                        {/* Кнопка генерации матчей — для всех типов лиг */}
                        <button
                          className="btn-generate-tour"
                          onClick={() => handleGenerateTour(stage)}
                        >
                          <FiPlus /> Сгенерировать матчи
                        </button>
                        
                        {/* Кнопка создания матча */}
                        <button
                          className="btn-create-match"
                          onClick={() => handleCreateMatch(stage)}
                        >
                          <FiPlus /> Создать матч
                        </button>
                      </div>
                      
                      {stageMatches[stage.id] ? (
                        stageMatches[stage.id].length > 0 ? (
                          <div className="stage-matches">
                            {stageMatches[stage.id].map((match) => (
                              <div
                                key={match.id}
                                className="match-row"
                                onClick={() => goToMatch(match.id)}
                              >
                                <div className="match-date">
                                  {match.date ? moment(match.date).format('DD.MM.YYYY') : '—'}
                                  {match.time && ` ${match.time}`}
                                </div>
                                <div className="match-teams">
                                  <span className="team-name">{match.home_team_name}</span>
                                  <span className="match-score">
                                    {match.status > 0 ? `${match.home_team_scores || 0}:${match.guest_team_scores || 0}` : 'vs'}
                                  </span>
                                  <span className="team-name">{match.guest_team_name}</span>
                                </div>
                                <div className="match-status">
                                  {renderMatchState(match)}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="no-matches">
                            <p>Матчи не найдены</p>
                          </div>
                        )
                      ) : (
                        <div className="stages-loading">
                          <Loader type="TailSpin" color="#3182ce" height={30} width={30} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-stages">
              <p>Стадии не найдены для выбранного сезона</p>
              <button
                className="btn-create-stage"
                onClick={() => setIsCreateStageModalOpen(true)}
              >
                <FiPlus /> Создать первую стадию
              </button>
            </div>
          )
        ) : (
          <div className="select-prompt">
            <p>Выберите лигу и сезон для просмотра стадий</p>
          </div>
        )}

        {/* Модальные окна */}
        <CreateStageModal
          isOpen={isCreateStageModalOpen}
          onClose={() => setIsCreateStageModalOpen(false)}
          onSuccess={handleStageCreated}
          league={selectedLeague}
          season={selectedSeason}
          stages={stages}
          format={currentFormat}
        />

        <CreateMultipleStagesModal
          isOpen={isCreateMultipleModalOpen}
          onClose={() => setIsCreateMultipleModalOpen(false)}
          onSuccess={handleStageCreated}
          league={selectedLeague}
          season={selectedSeason}
          stages={stages}
          format={currentFormat}
        />

        <EditStageModal
          isOpen={isEditStageModalOpen}
          onClose={() => setIsEditStageModalOpen(false)}
          onSuccess={handleStageCreated}
          stage={selectedStageForEdit}
          league={selectedLeague}
          season={selectedSeason}
          stages={stages}
          format={currentFormat}
        />

        <GenerateTourModal
          isOpen={isGenerateTourModalOpen}
          onClose={() => setIsGenerateTourModalOpen(false)}
          onSuccess={handleTourGenerated}
          stage={selectedStageForGenerate}
          league={selectedLeague}
          season={selectedSeason}
          format={currentFormat}
          stages={stages}
        />

        <CreateMatchModal
          isOpen={isCreateMatchModalOpen}
          onClose={() => setIsCreateMatchModalOpen(false)}
          onSuccess={handleMatchCreated}
          preselectedLeague={selectedLeague}
          preselectedSeason={selectedSeason}
          preselectedStage={selectedStageForMatch}
        />
      </div>
    </div>
  );
}

export default StagesManagement;
